import { createRoute, lazyRouteComponent, notFound } from '@tanstack/react-router';
import { z } from 'zod';
import { isApiError } from '@/api/errors';
import { appRoute } from '@/app/layout-routes';
import { requirePermissions } from '@/app/route-guards';
import { filterParam } from '@/lib/url-state';
import { roundQuery, roundsListQuery, roundsSearchSchema } from './api';
import { RoundNotFound } from './pages/round-not-found';

// Rounds reuse the GAMES permission bits (permissions.ts: ROUNDS).

export const settledRoundsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'rounds',
  validateSearch: roundsSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_GAMES'),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(roundsListQuery('settled', deps));
  },
  component: lazyRouteComponent(() => import('./pages/rounds-list-page'), 'SettledRoundsPage'),
});

export const liveRoundsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'rounds/live',
  validateSearch: roundsSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_GAMES'),
  staticData: { crumbs: () => [{ label: 'In progress' }] },
  component: lazyRouteComponent(() => import('./pages/rounds-list-page'), 'LiveRoundsPage'),
});

export const ROUND_TABS = ['overview', 'engine', 'bonus', 'replay'] as const;

export const roundDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'rounds/$roundId',
  validateSearch: z.object({ tab: filterParam.enum(ROUND_TABS) }),
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_GAMES'),
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(roundQuery(params.roundId));
    } catch (error) {
      // Another operator's round answers 404 too, on purpose.
      if (isApiError(error) && (error.code === 'not_found' || error.code === 'validation')) throw notFound();
      throw error;
    }
  },
  staticData: { crumbs: (params) => [{ label: `Round ${params.roundId ?? ''}` }] },
  notFoundComponent: RoundNotFound,
  component: lazyRouteComponent(() => import('./pages/round-detail-page'), 'RoundDetailPage'),
});

export const roundsRoutes = [settledRoundsRoute, liveRoundsRoute, roundDetailRoute] as const;
